// Saves a NEW item that was just created in a float/submission builder onto the
// candidate's main profile. A float keeps its own copy of each candidate, so
// without this the item would only ever exist in that one float.
//
// The posts themselves go through the profile page's own mutation hooks (see
// useSaveToProfile); this file maps builder items to the profile's fields and
// works out the profile ids that come back.

import { CURRENTLY_STUDYING } from "@lib/dataMap/education";
import stripHtml from "@lib/helpers/stripHtml";

export interface ProfileApi {
  post: (url: string, body?: unknown) => Promise<{ data: any }>;
}

const clean = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ) as Partial<T>;

// The profile only accepts a plain image URL (<= 255 chars) as a logo. Data URLs
// and uploaded blobs are skipped rather than failing the whole save.
const logoUrl = (logo: unknown): string | undefined =>
  typeof logo === "string" && /^https?:\/\//.test(logo) && logo.length <= 255 ? logo : undefined;

// The profile edits descriptions with the same rich-text editor as the
// builders, so the HTML is saved as it is. An emptied editor leaves "<p></p>",
// which isn't a description.
const richText = (html?: string) => (stripHtml(html).trim() ? html : undefined);

export const toProfileProject = (project: { name?: string; summary?: string }) =>
  clean({ name: project.name?.trim(), description: richText(project.summary) });

export const toProfileRole = (role: {
  name?: string;
  position?: string;
  period?: string;
  summary?: string;
  logo?: unknown;
}) =>
  clean({
    name: role.name?.trim(),
    role: role.position,
    time_period: role.period,
    description: richText(role.summary),
    image: logoUrl(role.logo),
  });

export const toProfileEducation = (education: {
  name?: string;
  provider?: string;
  period?: string;
  currently_studying?: boolean;
  logo?: unknown;
}) =>
  clean({
    course_name: education.name?.trim(),
    institution: education.provider?.trim(),
    year_completed: education.currently_studying ? CURRENTLY_STUDYING : education.period,
    logo: logoUrl(education.logo),
  });

/**
 * The name the "save to the main profile" switch shows: the real candidate's,
 * never the float's anonymised one. Undefined (no switch) without a profile.
 */
export const profileSwitchName = (profile?: { uuid?: string | null; name?: string | null } | null) =>
  profile?.uuid ? profile.name?.trim() || "the candidate" : undefined;

/** The builder item with the profile row's id in place of its temporary one. */
export const withProfileId = <T extends { id: number | string }>(
  item: T,
  saved?: { id?: number | string } | null,
): T => (saved?.id != null ? { ...item, id: saved.id } : item);

export interface SkillLike {
  id: number | string;
  name: string;
  type?: string;
  logo?: string | null;
}

export const isCustomSkill = (skill: SkillLike) => skill.type === "custom" || Number(skill.id) < 0;

export const sameSkillName = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Posts one skill to the profile and resolves with the candidate the API
 * returns, which includes `added_skill`: the skill the request attached.
 */
export type PostSkill = (
  body: { id: number | string } | { name: string; isCustom: true },
) => Promise<{ skills?: SkillLike[]; added_skill?: SkillLike } | undefined>;

/**
 * Adds skills to the profile one by one (the endpoint takes a single skill).
 * Custom skills come back with the profile skill's id, name, type and logo.
 * The API can resolve a typed name to a skill under another name ("NLP"), so
 * the skill it reports attaching is used; failing that, a skill with the same
 * name. A skill that fails to save keeps its temporary id and is listed in
 * `failed`; the rest are still saved.
 */
export async function saveSkillsToProfile<T extends SkillLike>(
  post: PostSkill,
  skills: T[],
): Promise<{ skills: T[]; failed: T[] }> {
  const saved: T[] = [];
  const failed: T[] = [];
  for (const skill of skills) {
    const custom = isCustomSkill(skill);
    try {
      const candidate = await post(custom ? { name: skill.name, isCustom: true } : { id: skill.id });
      const match = custom
        ? (candidate?.added_skill ?? candidate?.skills?.find((s) => sameSkillName(s.name, skill.name)))
        : undefined;
      saved.push(
        match
          ? { ...skill, id: match.id, name: match.name || skill.name, type: match.type ?? skill.type, logo: match.logo ?? skill.logo }
          : skill,
      );
    } catch {
      failed.push(skill);
      saved.push(skill);
    }
  }
  return { skills: saved, failed };
}

export interface PortfolioItem {
  id: string;
  title: string;
  url: string;
  type: "link" | "file" | "image" | "video";
  original_filename?: string | null;
  public_id?: string | null;
}

/**
 * Appends ONE item. Never replaces the profile's list, so nothing is deleted.
 * Resolves with the Axios response, whose `data` is the updated candidate.
 */
export async function savePortfolioItemToProfile(
  api: ProfileApi,
  candidateUuid: string,
  item: PortfolioItem,
): Promise<{ data: any }> {
  return api.post(
    `/candidate/${candidateUuid}/portfolio`,
    clean({
      id: String(item.id),
      title: item.title,
      url: item.url,
      type: item.type,
      original_filename: item.original_filename,
      public_id: item.public_id,
    }),
  );
}

export type CultureKey = "personality_summary" | "work_style" | "personal_story";

/**
 * The profile's culture fields hold the same rich-text HTML as a submission's,
 * capped by the API (markup included).
 */
export const PROFILE_CULTURE_MAX_LENGTH = 6000;
